import { lazy, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageErrorBoundary } from "./components/error-boundaries/PageErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminProtectedRoute } from "./components/AdminProtectedRoute";
import { LoadingBar } from "./components/LoadingBar";
import { VerificationPrompt } from "./components/VerificationPrompt";
import { DwellieDnaPrompt } from "./components/DwellieDnaPrompt";
import { ScrollToTop } from "./components/ScrollToTop";

// Create Sentry-wrapped router for performance tracking
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on mount if data is fresh
    },
  },
});

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Landing = lazy(() => import("./pages/Landing"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Auth = lazy(() => import("./pages/Auth"));
const OnboardingStart = lazy(() => import("./pages/onboarding/OnboardingStart"));
const OnboardingLookingFor = lazy(() => import("./pages/onboarding/OnboardingLookingFor"));
const OnboardingDetails = lazy(() => import("./pages/onboarding/OnboardingDetails"));
const OnboardingProfile = lazy(() => import("./pages/onboarding/OnboardingProfile"));
const OnboardingLifestyle = lazy(() => import("./pages/onboarding/OnboardingLifestyle"));
const OnboardingFinal = lazy(() => import("./pages/onboarding/OnboardingFinal"));
const OnboardingTrust = lazy(() => import("./pages/onboarding/OnboardingTrust"));
const Explore = lazy(() => import("./pages/Explore"));
const Spotlight = lazy(() => import("./pages/Spotlight"));
const Interested = lazy(() => import("./pages/Interested"));
const Connections = lazy(() => import("./pages/Connections"));
const Chat = lazy(() => import("./pages/Chat"));
const Verification = lazy(() => import("./pages/Verification"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfilePreferences = lazy(() => import("./pages/ProfilePreferences"));
const ProfileDetail = lazy(() => import("./pages/ProfileDetail"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DwellieDnaQuiz = lazy(() => import("./pages/DwellieDnaQuiz"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminVerifications = lazy(() => import("./pages/admin/AdminVerifications"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserProfile = lazy(() => import("./pages/admin/AdminUserProfile"));

// Lazy load BottomNav for code splitting
const BottomNav = lazy(() => import("./components/BottomNav").then(module => ({ default: module.BottomNav })));

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ScrollToTop />
          <VerificationPrompt />
          <DwellieDnaPrompt />
          <Suspense fallback={<LoadingBar />}>
            <SentryRoutes>
              <Route path="/" element={<PageErrorBoundary><Index /></PageErrorBoundary>} />
              <Route path="/landing" element={<PageErrorBoundary><Landing /></PageErrorBoundary>} />
              <Route path="/welcome" element={<PageErrorBoundary><Welcome /></PageErrorBoundary>} />
              <Route path="/auth" element={<PageErrorBoundary><Auth /></PageErrorBoundary>} />
              <Route path="/onboarding/start" element={<ProtectedRoute><PageErrorBoundary><OnboardingStart /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/onboarding/looking-for" element={<ProtectedRoute><PageErrorBoundary><OnboardingLookingFor /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/onboarding/details" element={<ProtectedRoute><PageErrorBoundary><OnboardingDetails /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/onboarding/profile" element={<ProtectedRoute><PageErrorBoundary><OnboardingProfile /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/onboarding/lifestyle" element={<ProtectedRoute><PageErrorBoundary><OnboardingLifestyle /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/onboarding/final" element={<ProtectedRoute><PageErrorBoundary><OnboardingFinal /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/onboarding/trust" element={<ProtectedRoute><PageErrorBoundary><OnboardingTrust /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/explore" element={<ProtectedRoute><PageErrorBoundary><Explore /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/spotlight" element={<ProtectedRoute><PageErrorBoundary><Spotlight /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/interested" element={<ProtectedRoute><PageErrorBoundary><Interested /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/connections" element={<ProtectedRoute><PageErrorBoundary><Connections /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/chat/:chatId" element={<ProtectedRoute><PageErrorBoundary><Chat /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/verification" element={<ProtectedRoute><PageErrorBoundary><Verification /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><PageErrorBoundary><Profile /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/profile/:userId" element={<ProtectedRoute><PageErrorBoundary><ProfileDetail /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/profile-preferences" element={<ProtectedRoute><PageErrorBoundary><ProfilePreferences /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/account-settings" element={<ProtectedRoute><PageErrorBoundary><AccountSettings /></PageErrorBoundary></ProtectedRoute>} />
              <Route path="/dwellie-dna-quiz" element={<ProtectedRoute><PageErrorBoundary><DwellieDnaQuiz /></PageErrorBoundary></ProtectedRoute>} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<PageErrorBoundary><AdminLogin /></PageErrorBoundary>} />
              <Route path="/admin/dashboard" element={<AdminProtectedRoute><PageErrorBoundary><AdminDashboard /></PageErrorBoundary></AdminProtectedRoute>} />
              <Route path="/admin/verifications" element={<AdminProtectedRoute><PageErrorBoundary><AdminVerifications /></PageErrorBoundary></AdminProtectedRoute>} />
              <Route path="/admin/reports" element={<AdminProtectedRoute><PageErrorBoundary><AdminReports /></PageErrorBoundary></AdminProtectedRoute>} />
              <Route path="/admin/users" element={<AdminProtectedRoute><PageErrorBoundary><AdminUsers /></PageErrorBoundary></AdminProtectedRoute>} />
              <Route path="/admin/users/:userId" element={<AdminProtectedRoute><PageErrorBoundary><AdminUserProfile /></PageErrorBoundary></AdminProtectedRoute>} />
              
              <Route path="*" element={<PageErrorBoundary><NotFound /></PageErrorBoundary>} />
            </SentryRoutes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
